import { logger } from '../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import { Protocol, PMTiles } from 'pmtiles'
import { Capacitor } from '@capacitor/core'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'

const log = logger.create('land')

const TILE_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''

const BLM_SMA_URL = `${TILE_ORIGIN}/data/land/blm-sma.pmtiles`
const PADUS_URL = `${TILE_ORIGIN}/data/land/padus-fee.pmtiles`

const BLM_SOURCE = 'blm-sma-source'
const BLM_FILL_LAYER = 'blm-sma-fill'
const BLM_LINE_LAYER = 'blm-sma-outline'

const PADUS_SOURCE = 'padus-source'
const PADUS_FILL_LAYER = 'padus-fill'
const PADUS_LINE_LAYER = 'padus-outline'

const DEFAULT_OPACITY = 0.4
const MIN_ZOOM = 3

export type PadusStyleMode = 'gap-status' | 'access' | 'owner-type'

const BLM_AGENCY_COLORS: maplibregl.ExpressionSpecification = [
  'match',
  ['get', 'ADMIN_AGENCY_CODE'],
  'BLM',
  '#F5DEB3',
  'FS',
  '#228B22',
  'NPS',
  '#8B4513',
  'FWS',
  '#4682B4',
  'BIA',
  '#CD853F',
  'BOR',
  '#87CEEB',
  'DOD',
  '#808080',
  'DOE',
  '#A9A9A9',
  'TVA',
  '#6495ED',
  'USACE',
  '#778899',
  '#D3D3D3',
]

const PADUS_GAP_COLORS: maplibregl.ExpressionSpecification = [
  'match',
  ['get', 'GAP_Sts'],
  '1',
  '#006400',
  '2',
  '#228B22',
  '3',
  '#90EE90',
  '4',
  '#FFFFE0',
  '#D3D3D3',
]

const PADUS_ACCESS_COLORS: maplibregl.ExpressionSpecification = [
  'match',
  ['get', 'Pub_Access'],
  'OA',
  '#2E8B57',
  'RA',
  '#DAA520',
  'XA',
  '#CD5C5C',
  '#A9A9A9',
]

const PADUS_OWNER_COLORS: maplibregl.ExpressionSpecification = [
  'match',
  ['get', 'Own_Type'],
  'FED',
  '#4682B4',
  'STAT',
  '#DAA520',
  'LOC',
  '#8FBC8F',
  'PVT',
  '#CD853F',
  'DIST',
  '#BC8F8F',
  'TRIB',
  '#D2691E',
  'UNK',
  '#C0C0C0',
  'JNT',
  '#9370DB',
  '#D3D3D3',
]

function getPadusColors(mode: PadusStyleMode): maplibregl.ExpressionSpecification {
  switch (mode) {
    case 'gap-status':
      return PADUS_GAP_COLORS
    case 'access':
      return PADUS_ACCESS_COLORS
    case 'owner-type':
      return PADUS_OWNER_COLORS
  }
}

class LandOwnershipService {
  private map: maplibregl.Map | null = null
  private protocol: Protocol | null = null
  private _blmEnabled = false
  private _padusEnabled = false
  private _blmOpacity = DEFAULT_OPACITY
  private _padusOpacity = DEFAULT_OPACITY
  private _padusStyleMode: PadusStyleMode = 'gap-status'

  get blmEnabled(): boolean {
    return this._blmEnabled
  }
  get padusEnabled(): boolean {
    return this._padusEnabled
  }
  get blmOpacity(): number {
    return this._blmOpacity
  }
  get padusOpacity(): number {
    return this._padusOpacity
  }
  get padusStyleMode(): PadusStyleMode {
    return this._padusStyleMode
  }

  getProtocol(): Protocol | null {
    return this.protocol
  }

  register(map: maplibregl.Map): void {
    this.map = map
    this.protocol = new Protocol()
    this.protocol.add(new PMTiles(BLM_SMA_URL))
    this.protocol.add(new PMTiles(PADUS_URL))
    maplibregl.addProtocol('pmtiles', this.protocol.tile)
    log.info('PMTiles protocol registered')
  }

  unregister(): void {
    this.disableBlm()
    this.disablePadus()
    try {
      maplibregl.removeProtocol('pmtiles')
    } catch (_) {
      /* may not be registered */
    }
    this.protocol = null
    this.map = null
  }

  // ── BLM SMA ──

  enableBlm(): void {
    if (!this.map || this._blmEnabled) return
    this._blmEnabled = true

    if (!this.map.getSource(BLM_SOURCE)) {
      this.map.addSource(BLM_SOURCE, {
        type: 'vector',
        url: `pmtiles://${BLM_SMA_URL}`,
        minzoom: MIN_ZOOM,
        maxzoom: 14,
      })
    }

    if (!this.map.getLayer(BLM_FILL_LAYER)) {
      addOverlayLayer(this.map, {
        id: BLM_FILL_LAYER,
        type: 'fill',
        source: BLM_SOURCE,
        'source-layer': 'blm_sma',
        minzoom: MIN_ZOOM,
        paint: {
          'fill-color': BLM_AGENCY_COLORS,
          'fill-opacity': this._blmOpacity,
        },
      }, 'blm-sma')
    }

    if (!this.map.getLayer(BLM_LINE_LAYER)) {
      addOverlayLayer(this.map, {
        id: BLM_LINE_LAYER,
        type: 'line',
        source: BLM_SOURCE,
        'source-layer': 'blm_sma',
        minzoom: MIN_ZOOM,
        paint: {
          'line-color': '#555555',
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.3, 10, 0.8, 14, 1.5],
          'line-opacity': Math.min(this._blmOpacity + 0.2, 1),
        },
      }, 'blm-sma')
    }
  }

  disableBlm(): void {
    if (!this.map) return
    this._blmEnabled = false
    if (this.map.getLayer(BLM_LINE_LAYER)) this.map.removeLayer(BLM_LINE_LAYER)
    if (this.map.getLayer(BLM_FILL_LAYER)) this.map.removeLayer(BLM_FILL_LAYER)
    if (this.map.getSource(BLM_SOURCE)) this.map.removeSource(BLM_SOURCE)
  }

  toggleBlm(): void {
    if (this._blmEnabled) this.disableBlm()
    else this.enableBlm()
  }

  setBlmOpacity(opacity: number): void {
    this._blmOpacity = Math.max(0, Math.min(1, opacity))
    if (this.map?.getLayer(BLM_FILL_LAYER)) {
      this.map.setPaintProperty(BLM_FILL_LAYER, 'fill-opacity', this._blmOpacity)
    }
    if (this.map?.getLayer(BLM_LINE_LAYER)) {
      this.map.setPaintProperty(BLM_LINE_LAYER, 'line-opacity', Math.min(this._blmOpacity + 0.2, 1))
    }
  }

  // ── PAD-US ──

  enablePadus(): void {
    if (!this.map || this._padusEnabled) return
    this._padusEnabled = true

    if (!this.map.getSource(PADUS_SOURCE)) {
      this.map.addSource(PADUS_SOURCE, {
        type: 'vector',
        url: `pmtiles://${PADUS_URL}`,
        minzoom: MIN_ZOOM,
        maxzoom: 14,
      })
    }

    if (!this.map.getLayer(PADUS_FILL_LAYER)) {
      addOverlayLayer(this.map, {
        id: PADUS_FILL_LAYER,
        type: 'fill',
        source: PADUS_SOURCE,
        'source-layer': 'padus',
        minzoom: MIN_ZOOM,
        paint: {
          'fill-color': getPadusColors(this._padusStyleMode),
          'fill-opacity': this._padusOpacity,
        },
      }, 'padus')
    }

    if (!this.map.getLayer(PADUS_LINE_LAYER)) {
      addOverlayLayer(this.map, {
        id: PADUS_LINE_LAYER,
        type: 'line',
        source: PADUS_SOURCE,
        'source-layer': 'padus',
        minzoom: MIN_ZOOM,
        paint: {
          'line-color': '#333333',
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.3, 10, 0.8, 14, 1.5],
          'line-opacity': Math.min(this._padusOpacity + 0.2, 1),
        },
      }, 'padus')
    }
  }

  disablePadus(): void {
    if (!this.map) return
    this._padusEnabled = false
    if (this.map.getLayer(PADUS_LINE_LAYER)) this.map.removeLayer(PADUS_LINE_LAYER)
    if (this.map.getLayer(PADUS_FILL_LAYER)) this.map.removeLayer(PADUS_FILL_LAYER)
    if (this.map.getSource(PADUS_SOURCE)) this.map.removeSource(PADUS_SOURCE)
  }

  togglePadus(): void {
    if (this._padusEnabled) this.disablePadus()
    else this.enablePadus()
  }

  setPadusOpacity(opacity: number): void {
    this._padusOpacity = Math.max(0, Math.min(1, opacity))
    if (this.map?.getLayer(PADUS_FILL_LAYER)) {
      this.map.setPaintProperty(PADUS_FILL_LAYER, 'fill-opacity', this._padusOpacity)
    }
    if (this.map?.getLayer(PADUS_LINE_LAYER)) {
      this.map.setPaintProperty(
        PADUS_LINE_LAYER,
        'line-opacity',
        Math.min(this._padusOpacity + 0.2, 1)
      )
    }
  }

  setPadusStyleMode(mode: PadusStyleMode): void {
    this._padusStyleMode = mode
    if (this.map?.getLayer(PADUS_FILL_LAYER)) {
      this.map.setPaintProperty(PADUS_FILL_LAYER, 'fill-color', getPadusColors(mode))
    }
  }

  // ── Re-add after style change ──

  reAdd(): void {
    if (!this.map) return
    if (this._blmEnabled) {
      if (!this.map.getSource(BLM_SOURCE)) {
        this.map.addSource(BLM_SOURCE, {
          type: 'vector',
          url: `pmtiles://${BLM_SMA_URL}`,
          minzoom: MIN_ZOOM,
          maxzoom: 14,
        })
      }
      if (!this.map.getLayer(BLM_FILL_LAYER)) {
        addOverlayLayer(
          this.map,
          {
            id: BLM_FILL_LAYER,
            type: 'fill',
            source: BLM_SOURCE,
            'source-layer': 'blm_sma',
            minzoom: MIN_ZOOM,
            paint: {
              'fill-color': BLM_AGENCY_COLORS,
              'fill-opacity': this._blmOpacity,
            },
          },
          'blm-sma'
        )
      }
      if (!this.map.getLayer(BLM_LINE_LAYER)) {
        addOverlayLayer(
          this.map,
          {
            id: BLM_LINE_LAYER,
            type: 'line',
            source: BLM_SOURCE,
            'source-layer': 'blm_sma',
            minzoom: MIN_ZOOM,
            paint: {
              'line-color': '#555555',
              'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.3, 10, 0.8, 14, 1.5],
              'line-opacity': Math.min(this._blmOpacity + 0.2, 1),
            },
          },
          'blm-sma'
        )
      }
    }
    if (this._padusEnabled) {
      if (!this.map.getSource(PADUS_SOURCE)) {
        this.map.addSource(PADUS_SOURCE, {
          type: 'vector',
          url: `pmtiles://${PADUS_URL}`,
          minzoom: MIN_ZOOM,
          maxzoom: 14,
        })
      }
      if (!this.map.getLayer(PADUS_FILL_LAYER)) {
        addOverlayLayer(
          this.map,
          {
            id: PADUS_FILL_LAYER,
            type: 'fill',
            source: PADUS_SOURCE,
            'source-layer': 'padus',
            minzoom: MIN_ZOOM,
            paint: {
              'fill-color': getPadusColors(this._padusStyleMode),
              'fill-opacity': this._padusOpacity,
            },
          },
          'padus'
        )
      }
      if (!this.map.getLayer(PADUS_LINE_LAYER)) {
        addOverlayLayer(
          this.map,
          {
            id: PADUS_LINE_LAYER,
            type: 'line',
            source: PADUS_SOURCE,
            'source-layer': 'padus',
            minzoom: MIN_ZOOM,
            paint: {
              'line-color': '#333333',
              'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.3, 10, 0.8, 14, 1.5],
              'line-opacity': Math.min(this._padusOpacity + 0.2, 1),
            },
          },
          'padus'
        )
      }
    }
  }

  // ── Popup query ──

  queryFeatures(point: maplibregl.Point): maplibregl.MapGeoJSONFeature[] {
    if (!this.map) return []
    const layers: string[] = []
    if (this._padusEnabled && this.map.getLayer(PADUS_FILL_LAYER)) layers.push(PADUS_FILL_LAYER)
    if (this._blmEnabled && this.map.getLayer(BLM_FILL_LAYER)) layers.push(BLM_FILL_LAYER)
    if (layers.length === 0) return []
    return this.map.queryRenderedFeatures(point, { layers })
  }

  buildPopupHtml(features: maplibregl.MapGeoJSONFeature[]): string | null {
    if (features.length === 0) return null
    const parts: string[] = []

    for (const f of features) {
      const p = f.properties
      if (f.layer.id === PADUS_FILL_LAYER) {
        const name = p.Unit_Nm ?? 'Unknown Unit'
        const desType = p.Des_Tp ?? ''
        const manager = p.Mang_Name ?? ''
        const access =
          p.Pub_Access === 'OA'
            ? 'Open'
            : p.Pub_Access === 'RA'
              ? 'Restricted'
              : p.Pub_Access === 'XA'
                ? 'Closed'
                : 'Unknown'
        const gap = p.GAP_Sts ? `GAP ${p.GAP_Sts}` : ''
        const acres = p.GIS_Acres ? `${Number(p.GIS_Acres).toLocaleString()} ac` : ''
        parts.push(
          `<div style="margin-bottom:8px">` +
            `<strong>${name}</strong>` +
            (desType ? `<br><em>${desType}</em>` : '') +
            (manager ? `<br>Manager: ${manager}` : '') +
            `<br>Access: ${access}` +
            (gap ? ` · ${gap}` : '') +
            (acres ? `<br>${acres}` : '') +
            `</div>`
        )
      } else if (f.layer.id === BLM_FILL_LAYER) {
        const agency = p.ADMIN_AGENCY_CODE ?? 'Unknown'
        const unit = p.ADMIN_UNIT_NAME ?? ''
        const state = p.ADMIN_ST ?? ''
        parts.push(
          `<div style="margin-bottom:8px">` +
            `<strong>Surface Management</strong>` +
            `<br>Agency: ${agency}` +
            (unit ? `<br>Unit: ${unit}` : '') +
            (state ? `<br>State: ${state}` : '') +
            `</div>`
        )
      }
    }

    return parts.length > 0 ? `<div class="cairn-land-popup">${parts.join('')}</div>` : null
  }
}

export const landOwnershipService = new LandOwnershipService()
